// src/components/Services.js
import React from "react";
import styled from "styled-components";
import {
  FaClipboardCheck,
  FaMicroscope,
  FaIndustry,
  FaGraduationCap,
  FaGlobe,
  FaCertificate,
  FaArrowRight,
  FaRobot,
} from "react-icons/fa";
import theme from "../styles/theme";

const ServicesSection = styled.section`
  padding: ${theme.spacing[24]} ${theme.spacing[4]};
  background: ${theme.secondary[50]};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[6]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[4]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[16]};
`;

const SectionTitle = styled.h2`
  font-size: clamp(${theme.typography.fontSize['2xl']}, 4vw, ${theme.typography.fontSize['4xl']});
  color: ${theme.secondary[900]};
  margin-bottom: ${theme.spacing[4]};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${theme.typography.fontWeight.bold};
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.secondary[600]};
  max-width: 700px;
  margin: 0 auto;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: ${theme.spacing[8]};
  margin-bottom: ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[10]};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.secondary[200]};
  transition: all ${theme.animation.normal} ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.primary};
    transform: scaleX(0);
    transition: transform ${theme.animation.normal} ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${theme.shadows['2xl']};
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${theme.gradients.primary};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize['2xl']};
  margin-bottom: ${theme.spacing[6]};
  transition: all ${theme.animation.normal} ease;

  ${ServiceCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  color: ${theme.secondary[900]};
  margin-bottom: ${theme.spacing[4]};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const ServiceDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.secondary[600]};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[6]};
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ServiceFeature = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.secondary[600]};
  font-weight: ${theme.typography.fontWeight.medium};

  &::before {
    content: "•";
    color: ${theme.primary[500]};
    font-weight: bold;
    font-size: ${theme.typography.fontSize.base};
  }
`;

const CTAButton = styled.button`
  background: ${theme.gradients.accent};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.animation.normal} ease;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  margin: 0 auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  }

  svg {
    transition: transform ${theme.animation.normal} ease;
  }

  &:hover svg {
    transform: translateX(2px);
  }
`;

const services = [
  {
    icon: <FaMicroscope />,
    title: "ISO 17025 Testing Laboratories",
    description:
      "Comprehensive accreditation consulting for testing laboratories worldwide. From initial gap analysis to full implementation and maintenance of ISO 17025:2017 standards.",
    features: [
      "Gap analysis and readiness assessment",
      "Quality management system development",
      "Technical competence evaluation",
      "Measurement uncertainty training",
      "Proficiency testing support",
      "International accreditation guidance",
    ],
  },
  {
    icon: <FaClipboardCheck />,
    title: "ISO 17020 Inspection Bodies",
    description:
      "Expert certification support for inspection bodies seeking ISO 17020 accreditation. Specializing in Type A, B, and C inspection body requirements globally.",
    features: [
      "Type A, B, C inspection body classification",
      "Competence and impartiality assessment",
      "Inspection procedure development",
      "Personnel qualification programs",
      "International conformity assessment",
      "Regulatory compliance guidance",
    ],
  },
  {
    icon: <FaIndustry />,
    title: "AISC Structural Fabricators",
    description:
      "Specialized consulting for structural steel fabricators pursuing AISC certification. Complete support for quality management and fabrication excellence.",
    features: [
      "AISC QC1 quality system implementation",
      "Welding procedure qualification",
      "Personnel certification support",
      "Fabrication process optimization",
      "Advanced special inspection",
      "International project compliance",
    ],
  },
  {
    icon: <FaGraduationCap />,
    title: "Training & Implementation",
    description:
      "Comprehensive training programs for international teams. Standard interpretation, implementation guidance, and internal audit capabilities development.",
    features: [
      "Standard interpretation workshops",
      "Implementation roadmap development",
      "Internal auditor training & certification",
      "Management system integration",
      "Cross-cultural training delivery",
      "Ongoing competence development",
    ],
  },
  {
    icon: <FaGlobe />,
    title: "International Compliance",
    description:
      "Global regulatory navigation and multi-jurisdictional compliance support. Ensuring your operations meet international standards across all markets.",
    features: [
      "Multi-country regulatory mapping",
      "International accreditation body liaison",
      "Cross-border certification support",
      "Regulatory change management",
      "Global best practice implementation",
      "24/7 international support network",
    ],
  },
  {
    icon: <FaCertificate />,
    title: "Audit & Assessment Services",
    description:
      "Independent third-party audits and assessments. Pre-accreditation reviews, gap analyses, and ongoing surveillance support for continuous improvement.",
    features: [
      "Pre-accreditation readiness audits",
      "Management system effectiveness reviews",
      "Technical competence assessments",
      "Risk-based audit approaches",
      "Corrective action verification",
      "Continuous improvement consulting",
    ],
  },
  {
    icon: <FaRobot />,
    title: "Workflow Optimization & AI-Enhanced Quality Systems",
    description:
      "Streamline your operations with customized workflow development and intelligent automation. We help testing laboratories, inspection bodies, manufacturers and fabricators implement software-driven solutions that align with ISO and AISC standards—boosting efficiency, traceability, and audit readiness.",
    features: [
      "Workflow mapping and process redesign",
      "Software selection and integration support (e.g., QMS platforms, audit tracking tools)",
      "AI-powered document control and data analysis",
      "Automation of corrective actions and audit scheduling",
      "Digital dashboards for real-time compliance monitoring",
      "ISO 17025 software integration",
    ],
  },
];

const Services = () => {
  return (
    <ServicesSection id="services">
      <Container>
        <SectionHeader>
          <SectionTitle>Global Accreditation Services</SectionTitle>
          <SectionSubtitle>
            Expert consulting for ISO 17025, ISO 17020, and AISC standards. 
            Comprehensive international support for laboratories, inspection bodies, 
            and structural fabricators worldwide.
          </SectionSubtitle>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, featureIndex) => (
                  <ServiceFeature key={featureIndex}>{feature}</ServiceFeature>
                ))}
              </ServiceFeatures>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <CTAButton
          onClick={() => {
            document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Start Your Accreditation Journey <FaArrowRight />
        </CTAButton>
      </Container>
    </ServicesSection>
  );
};

export default Services;
